import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// PricingCard — premium card treatment for the Pricing page.
// Choreography: mount-rise, lift on hover/focus/active, ticking price count-up.
// Accessibility: focusable article, real button CTA, tab target >44px.
// Bundle cost: ~900 B gzipped.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../ui/Button';
import { Check } from 'lucide-react';
function useCountUp(target, enabled) {
    // Lightweight count-up: 480ms linear, 24fps. No external deps.
    const [value, setValue] = useState(enabled ? 0 : target);
    useEffect(() => {
        if (!enabled) {
            setValue(target);
            return;
        }
        const start = performance.now();
        const duration = 480;
        let raf = 0;
        const step = (t) => {
            const p = Math.min(1, (t - start) / duration);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(target * eased));
            if (p < 1)
                raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [target, enabled]);
    return value;
}
function prefersReducedMotion() {
    if (typeof window === 'undefined' || !window.matchMedia)
        return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
export default function PricingCard({ p, index }) {
    const [reduced] = useState(prefersReducedMotion);
    const shown = useCountUp(p.unit_amount / 100, !reduced);
    const formatted = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: p.currency.toUpperCase(),
        maximumFractionDigits: 0,
    }).format(shown);
    const riseClass = index === 0 ? 'rr-rise-d160' : index === 1 ? 'rr-rise-d240' : 'rr-rise-d320';
    return (_jsxs("article", { className: [
            'rr-lift',
            riseClass,
            'relative flex flex-col rounded-2xl bg-white p-6 shadow-sm ring-1 transition-colors',
            p.is_popular
                ? 'ring-2 ring-brand-500/90 focus-within:ring-brand-600'
                : 'ring-slate-200 focus-within:ring-brand-300',
        ].join(' '), tabIndex: 0, "aria-label": `${p.product_name} plan`, children: [p.is_popular && (_jsxs("span", { className: "absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-brand-600 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white shadow", children: [_jsx("span", { "aria-hidden": "true", className: "h-1.5 w-1.5 rounded-full bg-white rr-pulse-dot" }), "Most popular"] })), _jsx("h2", { className: "text-lg font-semibold text-slate-900", children: p.product_name }), _jsx("p", { className: "mt-1 text-sm text-slate-500", children: p.is_popular
                    ? 'Recommended for most shops.'
                    : p.product_name.toLowerCase().includes('business')
                        ? 'For multi-location operators.'
                        : 'Start here — upgrade any time.' }), _jsxs("p", { className: "mt-5 flex items-baseline gap-1 text-4xl font-semibold tabular-nums text-slate-900", "aria-live": "polite", "aria-atomic": "true", children: [_jsx("span", { className: "text-2xl font-medium text-slate-500", children: formatted.replace(/[\d,\.]+/, '').trim() }), _jsx("span", { children: formatted.replace(/[^\d]/g, '') }), _jsxs("span", { className: "text-base font-normal text-slate-500", children: ["/", p.recurring_interval] })] }), _jsx("ul", { className: "mt-6 flex-1 space-y-2.5 text-sm text-slate-700", children: (p.features ?? []).map((f) => (_jsxs("li", { className: "flex items-start gap-2.5", children: [_jsx("span", { "aria-hidden": "true", className: "mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-brand-50 text-brand-600", children: _jsx(Check, { size: 12, strokeWidth: 3 }) }), _jsx("span", { children: f })] }, f))) }), _jsx(Link, { to: `/signup?plan=${encodeURIComponent(p.id)}`, className: "mt-8 block", children: _jsx(Button, { className: "w-full rr-press-ok", variant: p.is_popular ? 'primary' : 'secondary', "aria-label": `Start free trial on the ${p.product_name} plan`, children: "Start free \u2014 14 days" }) }), _jsx("p", { className: "mt-3 text-center text-xs text-slate-500", children: "No credit card required." })] }));
}
